import { toast } from 'react-toastify';
import { CreateOfferBody, Offer, UpdateOfferBody } from '@core/models/offer.model';
import { IErrorModel, ISuccessResponse } from '@core/models/generic.model';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import OfferService from 'services/api/offer.service';

// -------------------------------------------------------------------------

interface IOfferState {
    loading: boolean;
    addOfferLoading: boolean;
    updateOfferLoading: boolean;
    specificOfferLoading: boolean;
    error: IErrorModel | undefined;
    offers: Offer[];
    offer: Offer | null;
}

const initialState: IOfferState = {
    loading: false,
    addOfferLoading: false,
    updateOfferLoading: false,
    specificOfferLoading: false,
    error: {} as IErrorModel,
    offers: [],
    offer: null,
};

// -------------------------------------------------------------------------

export const getAllOffersThunk = createAsyncThunk<Offer[], void, { rejectValue: IErrorModel }>(
    `/offer/getAllOffers`,
    async (_, { rejectWithValue }: any) => {
        try {
            const response: any = await OfferService.getAllOffers();
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage, { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const getOfferByIdThunk = createAsyncThunk<Offer, number, { rejectValue: IErrorModel }>(
    `/offer/getOfferById`,
    async (id, { rejectWithValue }: any) => {
        try {
            const response: any = await OfferService.getOfferById(id);
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to fetch offer', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const addOfferThunk = createAsyncThunk<ISuccessResponse, CreateOfferBody, { rejectValue: IErrorModel }>(
    `/offer/addOffer`,
    async (body, { rejectWithValue, dispatch }: any) => {
        try {
            const response: any = await OfferService.addOffer(body);
            toast.success(response.data.message || 'Offer created successfully', { autoClose: 5000 });
            dispatch(getAllOffersThunk());
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage, { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const updateOfferThunk = createAsyncThunk<
    ISuccessResponse,
    { id: number; body: { numberOfStatic: string } },
    { rejectValue: IErrorModel }
>(`/offer/updateOffer`, async ({ id, body }, { rejectWithValue }: any) => {
    try {
        const response: any = await OfferService.updateOffer(id, body);
        toast.success(response.data.message || 'Offer updated successfully', { autoClose: 5000 });
        return response.data;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to update offer', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

export const deleteOfferThunk = createAsyncThunk<ISuccessResponse, number, { rejectValue: IErrorModel }>(
    `/offer/deleteOffer`,
    async (id, { rejectWithValue }: any) => {
        try {
            const response: any = await OfferService.deleteOffer(id);
            toast.success(response.data.message || 'Offer deleted successfully', { autoClose: 5000 });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to delete offer', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

// -------------------------------------------------------------------------

const offerSlice = createSlice({
    name: `offer`,
    initialState,
    reducers: {
        clearOffer(state) {
            state.offer = null;
        },
    },
    extraReducers: (builder) => {
        // Get All Offers
        builder.addCase(getAllOffersThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getAllOffersThunk.fulfilled, (state, action) => {
            state.loading = false;
            state.offers = action.payload;
        });
        builder.addCase(getAllOffersThunk.rejected, (state, action) => {
            state.loading = false;
            state.offers = [];
            state.error = action.payload;
        });

        // Get Offer By Id
        builder.addCase(getOfferByIdThunk.pending, (state) => {
            state.specificOfferLoading = true;
        });
        builder.addCase(getOfferByIdThunk.fulfilled, (state, action) => {
            state.specificOfferLoading = false;
            state.offer = action.payload;
        });
        builder.addCase(getOfferByIdThunk.rejected, (state, action) => {
            state.specificOfferLoading = false;
            state.error = action.payload;
        });

        // Add Offer
        builder.addCase(addOfferThunk.pending, (state) => {
            state.addOfferLoading = true;
        });
        builder.addCase(addOfferThunk.fulfilled, (state) => {
            state.addOfferLoading = false;
        });
        builder.addCase(addOfferThunk.rejected, (state, action) => {
            state.addOfferLoading = false;
            state.error = action.payload;
        });

        // Update Offer
        builder.addCase(updateOfferThunk.pending, (state) => {
            state.updateOfferLoading = true;
        });
        builder.addCase(updateOfferThunk.fulfilled, (state) => {
            state.updateOfferLoading = false;
        });
        builder.addCase(updateOfferThunk.rejected, (state, action) => {
            state.updateOfferLoading = false;
            state.error = action.payload;
        });

        // Delete Offer
        builder.addCase(deleteOfferThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(deleteOfferThunk.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(deleteOfferThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
    },
});

// -------------------------------------------------------------------------

export default offerSlice;
export const { clearOffer } = offerSlice.actions;
export const offerReducer = offerSlice.reducer;

